// The "My Jobs" list, ported from ionq-cloud-ui's MyJobs.tsx onto the
// console BFF: same columns in the same order, same URL-filter contract,
// same empty/error states — with the client-side pager replaced by
// virtualized infinite scrolling (JobsTable), and the project/organization
// lookups served by trpc instead of the public API.
//
// Deliberately absent vs the legacy page (documented in the README):
// bulk/single-job cancel and the QPU maintenance notice — the write path
// and the backend-status feed belong to cloud-job-manager / api-qpu-status,
// which the SQL-only BFF doesn't front. CSV export IS here (jobs.exportCsv
// recreates the download.csv endpoint), with one UX upgrade over the
// donor's single button: selected rows export from a Chakra ActionBar.

import {
    ActionBar,
    Box,
    Button,
    CloseButton,
    Flex,
    Icon,
    IconButton,
    Portal,
    Spinner,
    Text,
} from '@chakra-ui/react';
import {
    type StandardTableFeatures,
    scrollShellToTop,
    toaster,
} from '@ionq/legacy-ui';
import type { JobRow, JobsSortField } from '@ionq/trpc-console';
import {
    useInfiniteQuery,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import {
    createColumnHelper,
    type OnChangeFn,
    type RowSelectionState,
    type SortingState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdArrowOutward, MdRefresh } from 'react-icons/md';
import { useNavigate } from 'react-router';
import { Tooltip } from '../../components/Tooltip';
import { downloadTextFile } from '../../lib/download';
import {
    hasActiveFilters,
    MY_JOBS_FILTER_NAMES,
    mapUiFiltersToListFilters,
    useUrlFilters,
} from '../../lib/jobs/filters';
import {
    jobsExportCsvOptions,
    myJobsInfiniteOptions,
    organizationQueryOptions,
    projectsQueryOptions,
} from '../../lib/jobs/queries';
import { getNoiseModelName } from '../../lib/jobs/targets';
import {
    BackendCell,
    CostCell,
    DateCell,
    EstimatedCostCell,
    IdCell,
    NameCell,
    ProjectCell,
    StatusCell,
    TypeCell,
} from './cells';
import { FilterButton } from './FilterButton';
import { JobsTable } from './JobsTable';
import { EmptyFilterView, JobsEmptyView } from './page-chrome';
import { JobsRowActionPopover } from './RowActionPopover';
import { SelectAllRowsCell, SelectRowCell } from './SelectRowCell';
import { StatusTabs } from './StatusTabs';

const columnHelper = createColumnHelper<StandardTableFeatures, JobRow>();

// Module scope: JobsTable is memoized, so its props must be referentially
// stable across the owner's re-renders (selection, export state).
const getJobRowId = (row: JobRow) => row.id;

// Sortable-column ids → the contract's jobs.list sortBy fields.
const SORT_FIELD_BY_COLUMN: Record<string, JobsSortField> = {
    request: 'date',
    status: 'status',
    target: 'target',
    usage: 'cost',
};

export const MyJobs = () => {
    const navigate = useNavigate();
    const { filters, setFilters } = useUrlFilters(MY_JOBS_FILTER_NAMES);
    const listFilters = useMemo(
        () => mapUiFiltersToListFilters(filters),
        [filters],
    );

    // Server-side sort: the sortable headers drive the whole-set order — a
    // change swaps the query key, restarting the listing from page 1.
    // Column ids map onto the contract's sortBy fields.
    const [sorting, setSorting] = useState<SortingState>([
        { id: 'request', desc: true },
    ]);
    const order = sorting[0]?.desc === false ? 'asc' : 'desc';
    const sortBy = SORT_FIELD_BY_COLUMN[sorting[0]?.id ?? 'request'] ?? 'date';

    const jobsOptions = myJobsInfiniteOptions(listFilters, order, sortBy);
    const jobsQuery = useInfiniteQuery(jobsOptions);
    const organizationQuery = useQuery(organizationQueryOptions());
    const projectsQuery = useQuery(projectsQueryOptions());

    const queryClient = useQueryClient();
    // Fresh listing from page 1 (drops the stacked cursor pages — new jobs
    // land at the top in the current order); the filter options and the org
    // gates revalidate in the background.
    const reload = () => {
        void queryClient.resetQueries({ queryKey: jobsOptions.queryKey });
        void queryClient.invalidateQueries({
            queryKey: projectsQueryOptions().queryKey,
        });
        void queryClient.invalidateQueries({
            queryKey: organizationQueryOptions().queryKey,
        });
    };

    const hasAccess = !!organizationQuery.data?.hasAccessToPaidBackend;
    // Whole-set count from page 1 (the BFF computes it once per listing).
    const totalJobs = jobsQuery.data?.pages[0]?.total;

    // Row selection, keyed by job id (TanStack drops untoggled keys, so the
    // key set IS the selection — same shape the donor kept). A new listing
    // identity (filter or sort change) starts unselected: the old ids may
    // not even match the new filters. Adjust-during-render, not an effect —
    // a cached listing paints instantly, and an effect-time clear would
    // flash the previous selection and its action bar for a frame.
    const listingKey = `${JSON.stringify(listFilters)}:${sortBy}:${order}`;
    // A NEW listing identity restarts from page 1 — return the viewport to
    // the top with it. Lives HERE, above the key-remount boundary (JobsTable
    // remounts per listingKey), because a reset inside the table also fires
    // on Back-navigation mounts and zeroes the shell ScrollRestoration's
    // restored offset. Skipping the first render keeps initial mounts
    // (fresh loads AND Back) on the restored scroll.
    const previousListingKey = useRef(listingKey);
    useEffect(() => {
        if (previousListingKey.current === listingKey) return;
        previousListingKey.current = listingKey;
        scrollShellToTop();
    }, [listingKey]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    // Whole-filtered-set mode, entered from the action bar's "Select all N
    // rows": every loaded row stays selected (pages that land later fold in
    // below), and the action-bar export drops the ids param — the same
    // whole-set export as the top bar's button.
    const [selectAllMode, setSelectAllMode] = useState(false);
    const [prevListingKey, setPrevListingKey] = useState(listingKey);
    if (listingKey !== prevListingKey) {
        setPrevListingKey(listingKey);
        setRowSelection({});
        setSelectAllMode(false);
    }
    const selectedIds = Object.keys(rowSelection);

    // Any change driven through the table — a row toggle, the header
    // checkbox, a programmatic clear's write-back — re-scopes the selection
    // to explicit rows: leave whole-set mode.
    const handleRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
        (updater) => {
            setSelectAllMode(false);
            setRowSelection(updater);
        },
        [],
    );

    const clearSelection = () => {
        setRowSelection({});
        setSelectAllMode(false);
    };

    // Both export buttons drive the same donor endpoint recreation
    // (jobs.exportCsv): without `ids` the whole filtered set exports (the
    // top-bar button, or the action bar in select-all mode), with `ids` just
    // those rows. Action-bar exports clear the selection on success, like
    // the donor's. DIVERGENCE: the donor swallows failures to the console;
    // a toast is strictly more visible.
    const [exporting, setExporting] = useState<null | 'all' | 'selected'>(null);
    const exportJobs = async (selection?: { ids?: string[] }) => {
        setExporting(selection ? 'selected' : 'all');
        try {
            const csv = await queryClient.fetchQuery(
                jobsExportCsvOptions(listFilters, selection?.ids),
            );
            downloadTextFile('jobs.csv', csv, 'text/csv');
            if (selection) clearSelection();
        } catch {
            toaster.create({
                type: 'error',
                title: 'Export failed',
                description: 'The jobs CSV could not be generated.',
            });
        } finally {
            setExporting(null);
        }
    };

    // Flatten the cursor pages, de-duplicating by id: the upstream data can
    // re-emit a row across page boundaries when submitted_at ties (the
    // job-manager keyset bug) — and duplicate ids would break both React
    // keys and the virtualizer. Project names/units ride once per page
    // (payload dedup) and get folded into one lookup here.
    const { rows, projectsById } = useMemo(() => {
        const seen = new Set<string>();
        const flattened: JobRow[] = [];
        const lookup = new Map<
            string,
            { id: string; name: string; unit?: string }
        >();
        for (const page of jobsQuery.data?.pages ?? []) {
            for (const project of page.projects) {
                lookup.set(project.id, project);
            }
            for (const job of page.jobs) {
                if (seen.has(job.id)) continue;
                seen.add(job.id);
                flattened.push(job);
            }
        }
        return { rows: flattened, projectsById: lookup };
    }, [jobsQuery.data]);

    // Select-all mode covers rows that stream in later: whenever a page
    // lands, fold the new ids into the controlled selection (during render,
    // same pattern as the listing reset above, so freshly loaded rows never
    // paint an unchecked frame).
    if (selectAllMode && rows.some((row) => !rowSelection[row.id])) {
        setRowSelection(Object.fromEntries(rows.map((row) => [row.id, true])));
    }

    // The legacy column set, in order (MyJobs.tsx JOBS_TABLE_KEY_LABELS),
    // behind the donor's leading selection checkboxes. Date, Status,
    // Backend and Cost sort — server-side over the whole filtered set
    // (v9 only lets ACCESSOR columns carry sort state, hence the accessors
    // on cells that read row.original anyway; manualSorting never calls
    // them for ordering).
    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'select',
                // The header checkbox covers the rows loaded so far; the
                // action bar's "Select all N rows" upgrades to the whole
                // filtered set.
                header: ({ table }) => <SelectAllRowsCell table={table} />,
                // 40px = 16px control + the body cells' 12px paddings.
                meta: { width: '40px' },
                cell: ({ row }) => <SelectRowCell row={row} />,
            }),
            columnHelper.accessor('request', {
                header: 'Date',
                enableSorting: true,
                cell: (info) => <DateCell request={info.getValue()} />,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                enableSorting: true,
                // First click shows the ranking's prime face (completed >
                // canceled > … > submitted); string accessors would default
                // to ascending.
                sortDescFirst: true,
                cell: ({ row }) => <StatusCell status={row.original.status} />,
            }),
            columnHelper.display({
                id: 'name',
                header: 'Name',
                // The name is the row's link (rows are inert): NameCell
                // renders it as a router Link to the job detail, 'Untitled'
                // included.
                cell: ({ row }) => (
                    <NameCell
                        name={row.original.name}
                        width='301px'
                        errorMitigation={row.original.debias || false}
                        to={row.original.id}
                    />
                ),
            }),
            columnHelper.accessor('target', {
                id: 'target',
                header: 'Backend',
                enableSorting: true,
                cell: ({ row }) => (
                    <BackendCell
                        target={row.original.target}
                        noiseModel={row.original.noise_model}
                        noiseModelName={getNoiseModelName({
                            model: row.original.noise_model,
                        })}
                    />
                ),
            }),
            columnHelper.display({
                id: 'id',
                header: 'ID',
                cell: ({ row }) => <IdCell id={row.original.id} />,
            }),
            columnHelper.display({
                id: 'type',
                header: 'Type',
                cell: ({ row }) => (
                    <TypeCell
                        type={row.original.type}
                        circuits={row.original.circuits}
                    />
                ),
            }),
            columnHelper.display({
                id: 'project_name',
                header: 'Project',
                cell: ({ row }) => (
                    <ProjectCell
                        name={
                            row.original.project_id
                                ? projectsById.get(row.original.project_id)
                                      ?.name
                                : undefined
                        }
                    />
                ),
            }),
            columnHelper.display({
                id: 'estimated_cost',
                header: 'Estimated Cost',
                cell: ({ row }) => (
                    <EstimatedCostCell
                        estimatedCostAcu={row.original.estimated_cost_acu}
                        estimatedCostUsd={row.original.estimated_cost_usd}
                        hasAccessToPaidBackend={hasAccess}
                        unit={
                            row.original.project_id
                                ? projectsById.get(row.original.project_id)
                                      ?.unit
                                : undefined
                        }
                    />
                ),
            }),
            columnHelper.accessor((row) => row.cost_acu ?? row.cost_usd, {
                id: 'usage',
                header: 'Cost',
                enableSorting: true,
                cell: ({ row }) => (
                    <CostCell
                        pending={row.original.cost_pending}
                        costAcu={row.original.cost_acu}
                        costUsd={row.original.cost_usd}
                        hasAccessToPaidBackend={hasAccess}
                        unit={
                            row.original.project_id
                                ? projectsById.get(row.original.project_id)
                                      ?.unit
                                : undefined
                        }
                    />
                ),
            }),
            columnHelper.display({
                id: 'action',
                header: '',
                cell: ({ row }) => (
                    <JobsRowActionPopover
                        job={row.original}
                        onView={() =>
                            navigate(`${row.original.id}`, {
                                state: { isMyJobs: true },
                            })
                        }
                    />
                ),
            }),
        ],
        [hasAccess, projectsById, navigate],
    );

    const projectOptions = useMemo(
        () =>
            (projectsQuery.data ?? []).map((project) => ({
                value: project.id,
                label: project.name,
            })),
        [projectsQuery.data],
    );

    const { fetchNextPage: fetchNextJobsPage } = jobsQuery;
    const fetchNextPage = useCallback(
        () => void fetchNextJobsPage(),
        [fetchNextJobsPage],
    );
    const emptyView = useMemo(
        () =>
            hasActiveFilters(filters) ? <EmptyFilterView /> : <JobsEmptyView />,
        [filters],
    );

    // Errors: all three queries throw (the client default, lib/trpc.ts) into
    // the jobs branch's route errorElement (lib/shellRoutes.tsx) —
    // enterprise-style boundaries instead of the legacy inline error view.
    // Session errors don't throw: the expired-session dialog covers the
    // last-loaded page.

    return (
        <Flex direction='column' minH='0' flex='1'>
            <Flex mb='12px' align='center'>
                <StatusTabs filters={filters} setFilters={setFilters} />
                <FilterButton
                    filters={filters}
                    setFilters={setFilters}
                    projectOptions={projectOptions}
                />
                <Tooltip content='Reload'>
                    <IconButton
                        size='sm'
                        variant='ghost'
                        ml='8px'
                        onClick={reload}
                        disabled={jobsQuery.isFetching}
                        aria-label='Reload jobs'
                    >
                        <Icon
                            as={MdRefresh}
                            width='16px'
                            height='16px'
                            color='fg.muted'
                            animation={
                                jobsQuery.isFetching
                                    ? 'spin 1s linear infinite'
                                    : undefined
                            }
                        />
                    </IconButton>
                </Tooltip>
                <Tooltip content='Export'>
                    <IconButton
                        size='sm'
                        variant='ghost'
                        ml='8px'
                        onClick={() => void exportJobs()}
                        disabled={exporting !== null}
                        aria-label='Export jobs'
                    >
                        {exporting === 'all' ? (
                            <Spinner size='sm' color='fg.muted' />
                        ) : (
                            <Icon
                                as={MdArrowOutward}
                                width='16px'
                                height='16px'
                                color='fg.muted'
                            />
                        )}
                    </IconButton>
                </Tooltip>
                <Box flex='1' />
                <Text textStyle='body-8' color='fg.muted'>
                    {totalJobs === undefined
                        ? ''
                        : `${totalJobs} ${totalJobs === 1 ? 'Job' : 'Jobs'}`}
                </Text>
            </Flex>
            <JobsTable
                // Listing identity: a new filter/order listing remounts the
                // table so instance state that survives controlled-selection
                // clears — v9's shift-range anchor (_lastSelectedRowId) —
                // can't range against a row from the previous listing.
                key={listingKey}
                data={rows}
                columns={columns}
                sorting={sorting}
                onSortingChange={setSorting}
                // Compact rows: 20px content band + 10px above and below.
                rowHeight={40}
                getRowId={getJobRowId}
                isLoading={jobsQuery.isPending}
                // An error the page survives (a session error; the rest
                // throw) must stop the infinite trigger: the failed page
                // keeps its cursor, so it would be re-requested forever.
                hasNextPage={jobsQuery.hasNextPage && !jobsQuery.isError}
                isFetchingNextPage={jobsQuery.isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                totalRows={totalJobs}
                emptyView={emptyView}
                rowSelection={rowSelection}
                onRowSelectionChange={handleRowSelectionChange}
            />
            {/* Selected-rows export (chakra-ui.com/docs/components/action-bar):
                floats over the table while anything is checked; closing it
                (or exporting) deselects everything. */}
            <ActionBar.Root
                open={selectedIds.length > 0 || selectAllMode}
                onOpenChange={({ open }) => {
                    if (!open) clearSelection();
                }}
                closeOnInteractOutside={false}
            >
                <Portal>
                    <ActionBar.Positioner>
                        <ActionBar.Content>
                            {/* One 36px line for every control — the stock
                                recipe mixes heights (trigger 50 / button 28)
                                and reads ragged. */}
                            <ActionBar.SelectionTrigger h='36px'>
                                {selectAllMode
                                    ? `All ${totalJobs} selected`
                                    : `${selectedIds.length} selected`}
                            </ActionBar.SelectionTrigger>
                            <ActionBar.Separator />
                            {/* Whole-set upgrade: appears while the selection
                                is narrower than the filtered set. */}
                            {!selectAllMode &&
                                totalJobs !== undefined &&
                                selectedIds.length < totalJobs && (
                                    <Button
                                        size='sm'
                                        h='36px'
                                        variant='outline'
                                        onClick={() => setSelectAllMode(true)}
                                        disabled={exporting !== null}
                                    >
                                        Select all {totalJobs} rows
                                    </Button>
                                )}
                            <Button
                                size='sm'
                                h='36px'
                                variant='outline'
                                onClick={() =>
                                    void exportJobs(
                                        selectAllMode
                                            ? {}
                                            : { ids: selectedIds },
                                    )
                                }
                                disabled={exporting !== null}
                            >
                                <Icon
                                    as={MdArrowOutward}
                                    width='16px'
                                    height='16px'
                                    color='fg.muted'
                                />
                                {exporting === 'selected'
                                    ? 'Exporting'
                                    : 'Export'}
                                {exporting === 'selected' && (
                                    <Spinner size='sm' ml='0.3em' />
                                )}
                            </Button>
                            {/* Closes the bar; onOpenChange clears the
                                selection with it. */}
                            <ActionBar.CloseTrigger asChild>
                                <CloseButton size='sm' h='36px' w='36px' />
                            </ActionBar.CloseTrigger>
                        </ActionBar.Content>
                    </ActionBar.Positioner>
                </Portal>
            </ActionBar.Root>
        </Flex>
    );
};
